import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useHistory, useLocation} from "react-router-dom";
import Paper from "@mui/material/Paper";
import {Container, Tooltip} from "@mui/material";
import {ThemeProvider as MaterialThemeProvider} from "@mui/material/styles";
import {ThemeProvider as StylesThemeProvider} from "@mui/styles";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from '@mui/material/Alert';
import IconButton from "@mui/material/IconButton";
import VisibilityTwoToneIcon from '@mui/icons-material/VisibilityTwoTone';
import VisibilityOffTwoToneIcon from '@mui/icons-material/VisibilityOffTwoTone';
import Link from "@mui/material/Link";
import Background from "../../../assets/atlantis_bg.jpg";
import logo from "../../../assets/v2/atlantis_logo.svg";
import {LoginStyle} from "../../../styles/pages/auth/LoginStyle";
import CustomText from "../../widgets/inputs/CustomText";
import {codFRegisterLogin, companyUsername} from "../../../utils/RegisterUtils";
import CustomTextWithAdornement from "../../widgets/inputs/CustomTextWithAdornement";
import {AppContext, AuthContext} from "../../../App";
import Copyright from "../utils/Copyright";
import {setCloseSnackbar, setOpenSnackbar} from "../../../services/AppServices";
import {loginUser} from "../../../services/AuthServices";
import {ForgotPasswordDialog} from "../utils/ForgotPasswordDialog";
import {BaseStyle} from "../../../styles/shared/BaseStyle";
import clsx from "clsx";
import {createAtlantisTheme} from "../../../styles/theme/AtlantisTheme";

export const LoginForm = () => {
    const classes = LoginStyle();
    const baseClasses = BaseStyle();
    const appContext = useContext(AppContext);
    const authContext = useContext(AuthContext);
    const {register, handleSubmit, errors, control} = useForm({mode: "onChange"});
    const [submit, setSubmit] = useState(false);
    const [type, setType] = useState("password");
    const [openForgot, setOpenForgot] = useState(false);
    const history = useHistory();
    const location = useLocation();
    const loginTheme = useMemo(() => createAtlantisTheme({type: "dark"}), []);

    const onSubmit = useCallback((data) => {
        setSubmit(true);
        loginUser(data, authContext.dispatch, location, history)
    }, [authContext.dispatch, location, history]);

    const handleRegister = useCallback(() => {
        if (history.location.pathname.includes("aziendeospedaliere")) {
            history.push("/aziendeospedaliere/register")
        } else if (history.location.pathname.includes("sisp")) {
            history.push("/sisp/register")
        } else {
            history.push("/register")
        }
    }, [history])

    const handleChangeType = useCallback(() => {
        if (type === "password") {
            setType("text");
        } else {
            setType("password");
        }
    }, [type])

    useEffect(() => {
        if (!authContext.state.loadingLogin && authContext.state.errorLogin === "") {
            setSubmit(false);
            setOpenSnackbar("success", "Login avvenuto con successo", appContext.dispatch);
        } else if (!authContext.state.loadingLogin && authContext.state.errorLogin.length > 1) {
            setSubmit(false);
            setOpenSnackbar("error", authContext.state.errorLogin, appContext.dispatch);
        }
    }, [authContext.state.loadingLogin, authContext.state.errorLogin, appContext.dispatch, history])

    useEffect(() => {
        document.body.style.backgroundImage = `url(${Background})`;
        document.body.style.backgroundAttachment = "fixed";
        document.body.style.backgroundSize = "cover";
        document.body.style.backgroundPosition = "center";

        return () => {
            document.body.removeAttribute("style");
        };
    }, []);

    const handleCloseSnack = useCallback(() => {
        setCloseSnackbar(appContext.dispatch);
    }, [appContext.dispatch]);

    return (
        <MaterialThemeProvider theme={loginTheme}>
            <StylesThemeProvider theme={loginTheme}>
                <Container maxWidth={"md"} className={classes.container} component={"main"}>
                    <Paper variant={"elevation"}
                           className={clsx(classes.paper, baseClasses.roundBorder, baseClasses.shadow24)}
                           component={"article"}>
                        <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
                            <Box className={classes.loginStack}>
                                <Box className={classes.logoBlock}>
                                    <img className={classes.loginLogo} src={logo} alt={"logo"}/>
                                </Box>
                                {/*<Grid item xs={12}>*/}
                                {/*	<Typography className={classes.title} variant={"h3"}><b>Atlantis</b></Typography>*/}
                                {/*</Grid>*/}
                                <Typography className={classes.subTitle} variant={"body1"}>
                                    Sistema per la gestione delle richieste di assistenza dei clienti
                                </Typography>

                                <Box className={classes.fieldStack}>
                                    {
                                        (history.location.pathname.includes("me")) ?
                                            <CustomText
                                                control={control}
                                                rules={codFRegisterLogin}
                                                name={"codF"}
                                                label={"Codice fiscale"}
                                                autoFocus={true}
                                                autcomplete={"taxCode"}
                                                error={errors["codF"]}
                                                className={clsx(baseClasses.borderInput, classes.loginField)}
                                                inputProps={{
                                                    className: clsx(classes.input, classes.noAutocompleteStyle)
                                                }}
                                            /> :
                                            <CustomText
                                                control={control}
                                                // rules={companyUsername}
                                                name={"codF"}
                                                label={<span style={{color: "#FFF"}}>Username</span>}
                                                autoFocus={true}
                                                error={errors["codF"]}
                                                className={clsx(baseClasses.borderInput, classes.loginField)}
                                                inputProps={{
                                                    className: clsx(classes.input, classes.noAutocompleteStyle)
                                                }}
                                            />
                                    }
                                    <CustomTextWithAdornement
                                        inputRef={register({required: true})}
                                        name={"password"}
                                        label={<span style={{color: "#FFF"}}>Password</span>}
                                        autoFocus={false}
                                        autoComplete={"password"}
                                        error={errors["password"]}
                                        className={clsx(baseClasses.borderInput, classes.loginField)}
                                        type={type}
                                        inputProps={{
                                            className: clsx(classes.noAutocompleteStyle)
                                        }}
                                        adornement={<Tooltip title={"Mostra / nascondi password"}>
                                            <IconButton onClick={() => handleChangeType()} size="large">
                                                {
                                                    type === "password" ?
                                                        <VisibilityTwoToneIcon style={{color: "#FFF"}}/> :
                                                        <VisibilityOffTwoToneIcon style={{color: "#FFF"}}/>
                                                }
                                            </IconButton>
                                        </Tooltip>}
                                        handleKeyDown={() => {
                                        }}
                                        handleKeyPress={() => {
                                        }}
                                    />

                                    <Button
                                        type="submit"
                                        fullWidth={true}
                                        variant="contained"
                                        disableElevation
                                        color={"primary"}
                                        onClick={handleSubmit(onSubmit)}
                                        className={clsx(classes.submit)}
                                        disabled={submit}
                                    >
                                        {
                                            submit ?
                                                <CircularProgress size={24}/> :
                                                <React.Fragment><b>Accedi</b></React.Fragment>
                                        }
                                    </Button>
                                </Box>

                                <Box className={classes.linkStack}>
                                    <Link
                                        className={classes.link}
                                        variant="body2"
                                        color={"inherit"}
                                        onClick={() => setOpenForgot(true)}
                                    >
                                        Password dimenticata?
                                    </Link>
                                    <span>
                                        Non hai un account?{" "}
                                        <Link
                                            className={classes.link}
                                            variant="body2"
                                            color={"inherit"}
                                            onClick={handleRegister}
                                        ><b>Registrati</b>
                                        </Link>
                                    </span>
                                </Box>
                            </Box>
                        </form>
                        <Box mt={2}>
                            <Copyright isLogin={true}/>
                        </Box>
                    </Paper>
                </Container>
                <ForgotPasswordDialog open={openForgot} handleClose={() => setOpenForgot(!openForgot)}/>
                <Snackbar open={appContext.state.open} autoHideDuration={3000} onClose={handleCloseSnack}>
                    <Alert style={{borderRadius: 28}} onClose={handleCloseSnack}
                           severity={appContext.state.severity}>
                        {appContext.state.message}
                    </Alert>
                </Snackbar>
            </StylesThemeProvider>
        </MaterialThemeProvider>
    );
}

function Alert(props) {
    return <MuiAlert elevation={16} variant="filled" {...props} />;
}
